import { styled } from "../../../styles";

// The builder header (62px) is absolutely positioned; the tab bar below it takes another 62px.
export const ScrollableContainer = styled("div", {
  width: "100%",
  height: "calc(100vh - 124px)",
  overflowY: "auto",

  display: "flex",
  justifyContent: "center",

  position: "relative",

  variants: {
    noTabs: {
      true: {
        marginTop: "62px",
        height: "calc(100vh - 62px)",
      },
    },
  },
});

export const BodyContainer = styled("div", {
  minWidth: "1020px",
  width: "60%",
  height: "max-content",
  minHeight: "100%",

  backgroundColor: "$white",
  border: "1px solid $gray-40",

  padding: "32px 48px 48px 48px",

  display: "flex",
  flexDirection: "column",
  gap: "24px",
});

export const TabPanel = styled("div", {
  "&[hidden]": { display: "none" },
});

/** The grey cards of the design: survey details, each question, the graph settings. */
export const Panel = styled("div", {
  position: "relative",
  backgroundColor: "$gray-30",
  borderRadius: "8px",
  padding: "28px 36px",

  display: "flex",
  flexDirection: "column",
  gap: "16px",

  variants: {
    question: {
      true: {
        borderLeft: "3px solid $secondary",
      },
    },
  },
});

export const PanelTitle = styled("h3", {
  margin: 0,
  fontFamily: "$openSans",
  fontWeight: 600,
  fontSize: "16px",
  color: "$charcoal",
});

export const PanelHeader = styled("div", {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "16px",
});

/** A card title with its tags beside it. */
export const TitleGroup = styled("div", {
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  gap: "12px",
});

export const PanelActions = styled("div", {
  display: "flex",
  alignItems: "center",
  gap: "8px",
});

/** A label on the left, its input on the right (the design's form rows). */
export const FieldRow = styled("label", {
  display: "grid",
  gridTemplateColumns: "160px minmax(0, 1fr)",
  alignItems: "center",
  columnGap: "24px",

  fontFamily: "$openSans",
  fontSize: "14px",
  color: "$charcoal",
});

export const FieldRowPair = styled("div", {
  display: "flex",
  flexWrap: "wrap",
  gap: "16px 48px",

  [`& ${FieldRow}`]: {
    flex: "1 1 320px",
  },
});

export const Footer = styled("div", {
  display: "flex",
  justifyContent: "flex-end",
  gap: "12px",
});

export const AddButtons = styled("div", {
  display: "flex",
  flexWrap: "wrap",
  gap: "8px",
});

/** The design's "Add question" (dark) and "Add info page" (light) pills. */
export const PillButton = styled("button", {
  minHeight: "38px",
  padding: "6px 22px",
  borderRadius: "20px",
  cursor: "pointer",
  fontFamily: "$openSans",
  fontSize: "14px",
  fontWeight: 600,

  "&:disabled": { opacity: 0.4, cursor: "not-allowed" },

  // Tenant colours: the portal's main button, and the builder's secondary one.
  variants: {
    tone: {
      dark: {
        backgroundColor: "var(--colors-button-background)",
        border: "2px solid var(--colors-button-border)",
        color: "var(--colors-button-text)",
        "&:hover:not(:disabled)": {
          backgroundColor: "var(--colors-button-hover-background)",
          borderColor: "var(--colors-button-hover-border)",
        },
        "&:focus-visible": {
          outline: "2px solid var(--colors-button-focus-border)",
        },
      },
      light: {
        backgroundColor: "var(--colors-builder-button-secondary-background)",
        border: "2px solid var(--colors-builder-button-primary-background)",
        color: "var(--colors-builder-button-secondary-text)",
        "&:hover:not(:disabled)": {
          backgroundColor:
            "var(--colors-builder-button-secondary-hover-background)",
        },
        "&:focus-visible": {
          outline:
            "2px solid var(--colors-builder-button-secondary-focus-border)",
        },
      },
    },
  },
  defaultVariants: { tone: "dark" },
});

export const Chips = styled("div", {
  display: "flex",
  flexWrap: "wrap",
  gap: "8px",
});

export const Chip = styled("span", {
  display: "inline-flex",
  alignItems: "center",
  gap: "6px",
  padding: "4px 12px",
  borderRadius: "16px",
  backgroundColor: "$secondary-lightest",
  color: "$secondary-dark",
  fontFamily: "$openSans",
  fontSize: "13px",

  button: {
    border: "none",
    background: "none",
    cursor: "pointer",
    color: "inherit",
    padding: 0,
    lineHeight: 1,
  },
});

/** Makes a shared input component (e.g. TextArea) fill its grid cell. */
export const FullWidth = styled("div", {
  width: "100%",
  "& > *, textarea": { width: "100% !important" },
});

export const DomainEditor = styled("div", {
  display: "flex",
  flexDirection: "column",
  gap: "12px",
});

export const DomainInputRow = styled("div", {
  display: "grid",
  gridTemplateColumns: "minmax(0, 1fr) max-content",
  alignItems: "start",
  gap: "12px",
});

/** A saved value in a card's read-only view. */
export const ValueText = styled("p", {
  margin: 0,
  fontFamily: "$openSans",
  fontSize: "14px",
  color: "$charcoal",
  overflowWrap: "anywhere",

  variants: {
    muted: { true: { color: "$gray-90" } },
  },
});

export const ErrorText = styled("p", {
  margin: 0,
  color: "$error",
  fontFamily: "$openSans",
  fontSize: "13px",
});

export const MutedText = styled("p", {
  margin: 0,
  color: "$gray-100",
  fontFamily: "$openSans",
  fontSize: "13px",
});

export const IconButton = styled("button", {
  minWidth: "32px",
  minHeight: "32px",
  padding: "4px 12px",
  borderRadius: "16px",
  border: "1px solid var(--colors-builder-button-primary-background)",
  backgroundColor: "var(--colors-builder-button-secondary-background)",
  color: "var(--colors-builder-button-secondary-text)",
  cursor: "pointer",
  fontFamily: "$openSans",
  fontSize: "13px",
  fontWeight: 600,

  "&:hover:not(:disabled)": {
    backgroundColor: "var(--colors-builder-button-secondary-hover-background)",
  },
  "&:focus-visible": {
    outline: "2px solid var(--colors-builder-button-secondary-focus-border)",
  },
  "&:disabled": {
    opacity: 0.35,
    cursor: "not-allowed",
  },
});

/** A published version can't be edited; this fieldset disables the inputs inside it. */
export const ReadOnlyFieldset = styled("fieldset", {
  border: "none",
  margin: 0,
  padding: 0,
  minWidth: 0,
});
